import { Link, createFileRoute } from '@tanstack/react-router';
import { Sparkles, Calendar, MessageSquare, TrendingUp, ShieldCheck, Zap } from 'lucide-react';

import { PromoCounter } from '../components/PromoCounter';

export const Route = createFileRoute('/')({
  component: HomePage,
});

function HomePage() {
  return (
    <>
      {/* Promo banner — live counter */}
      <div className="border-b border-accent/20 bg-accent-soft">
        <div className="mx-auto flex max-w-6xl items-center justify-center px-6 py-3">
          <PromoCounter variant="banner" />
        </div>
      </div>

      {/* Hero */}
      <section className="px-6 pt-16 pb-12 md:pt-24 md:pb-20">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-bg px-4 py-1.5 text-xs font-medium text-text ring-1 ring-border">
            <Sparkles className="size-3.5 text-accent" aria-hidden="true" />
            Promocja założycielska — im wcześniej tym taniej
          </span>
          <h1 className="mt-6 font-display text-4xl font-semibold tracking-tight text-text md:text-6xl">
            Cena jednego miesiąca,<br />
            <span className="text-accent">wiele miesięcy działania.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-pretty text-lg text-text-muted md:text-xl">
            Pierwsze 10 klinik dostaje cały rok za 99 zł. Kolejne 90 — 6 miesięcy
            za 99 zł. Pozostałe miejsca w pierwszym tysiącu — 3 miesiące. Potem
            standard: 99 zł/mies.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/start"
              className="rounded-full bg-text px-6 py-3 text-base font-medium text-bg hover:bg-text/90"
            >
              Zajmij miejsce →
            </Link>
            <Link
              to="/cennik"
              className="rounded-full bg-surface px-6 py-3 text-base font-medium text-text ring-1 ring-border hover:ring-text/20"
            >
              Zobacz cennik
            </Link>
          </div>
          <p className="mt-4 text-xs text-text-subtle">
            Płacisz 99 zł raz — dostajesz tyle miesięcy, w którym pakiecie się
            zarejestrujesz. Bez automatycznych odnowień. Bez prowizji.
          </p>

          {/* Trust ladder */}
          <ul className="mx-auto mt-10 grid max-w-2xl grid-cols-3 gap-2 text-left md:gap-4">
            {[
              { n: '99 zł', label: 'jednorazowo, bez karty' },
              { n: 'do 12', label: 'miesięcy w pakiecie założyciela' },
              { n: '0 zł', label: 'prowizji od wizyt' },
            ].map((s) => (
              <li
                key={s.label}
                className="rounded-xl bg-bg p-4 ring-1 ring-border"
              >
                <p className="font-display text-2xl font-semibold text-text">{s.n}</p>
                <p className="mt-1 text-xs text-text-muted">{s.label}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Value props */}
      <section className="px-6 py-16 bg-surface/60 border-y border-border">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-text md:text-4xl">
            Wszystko, co potrzeba do pozyskiwania pacjentów online
          </h2>
          <p className="mt-3 text-center text-text-muted">
            W jednym pakiecie. Bez doklejania per-funkcję jak u konkurencji.
          </p>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <ValueCard
              icon={Sparkles}
              title="Strona dopasowana do Twojej kliniki"
              body="Wypełnij krótki kwestionariusz. W kilka minut otrzymasz kompletną stronę z hero, opisem zabiegów, FAQ i opiniami — wszystko zgodne z prawem reklamy medycznej (UoKiK, KIWL, KIDL)."
            />
            <ValueCard
              icon={Calendar}
              title="Kalendarz rezerwacji"
              body="Pacjent rezerwuje wizytę 24/7 bez wychodzenia z Twojej strony. Bez prowizji od wizyt. Powiadomienia SMS T-24h, anulowanie jednym klikiem, przeniesienie wizyty między lekarzami."
            />
            <ValueCard
              icon={MessageSquare}
              title="Drip kampanie SMS"
              body="Potwierdzenia, przypomnienia, recall po 6 miesiącach. Automatyczne prośby o opinię na Google. Integracja z smsapi.pl, respekt dla STOP i RODO."
            />
            <ValueCard
              icon={TrendingUp}
              title="Content marketplace"
              body="Cykliczne wpisy blogowe, strony lokalne typu <usługa> w <miasto>, backlinki DR40+ — kupowane jak abonament, dostarczane przez nasz zespół redakcyjny."
            />
            <ValueCard
              icon={ShieldCheck}
              title="RODO + reklama medyczna"
              body="Zgody w wersji per-tenant, audit log z hash-chainem, automatycznie generowana polityka prywatności. Forbidden-claims regex blokuje gwarancje, superlatywy, fałszywe obietnice."
            />
            <ValueCard
              icon={Zap}
              title="Własna domena"
              body="W naszej domenie *.pozyskajpacjenta.pl od razu, własna domena (z naszą pomocą lub Twoja) — z automatycznym SSL. Bez per-feature opłat."
            />
          </div>
        </div>
      </section>

      {/* Why us vs them */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-text md:text-4xl">
            Czym różnimy się od ZnanyLekarz / Booksy / MyDr
          </h2>
          <div className="mt-10 overflow-hidden rounded-2xl ring-1 ring-border">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wide text-text-subtle">
                <tr>
                  <th className="px-4 py-3 font-semibold">Co dostajesz</th>
                  <th className="px-4 py-3 font-semibold">ZnanyLekarz Starter</th>
                  <th className="px-4 py-3 font-semibold">MyDr Optymalny</th>
                  <th className="px-4 py-3 font-semibold bg-accent/5 text-accent">Pozyskaj</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-bg">
                <Row
                  label="Cena miesięczna"
                  znl="399 zł"
                  mydr="129 zł"
                  ours="99 zł"
                />
                <Row
                  label="Prowizja od wizyty pacjenta"
                  znl="26–32 zł"
                  mydr="1.49–2.99%"
                  ours="0 zł"
                />
                <Row
                  label="Własna strona (nie tylko profil)"
                  znl="✗"
                  mydr="✗"
                  ours="✓"
                />
                <Row
                  label="Treść SEO auto-generowana"
                  znl="✗"
                  mydr="✗"
                  ours="✓"
                />
                <Row
                  label="Strony per zabieg"
                  znl="✗"
                  mydr="✗"
                  ours="✓"
                />
                <Row
                  label="Backlinki + GBP sync (add-on)"
                  znl="—"
                  mydr="—"
                  ours="✓"
                />
                <Row
                  label="Rezerwacja online 24/7"
                  znl="✓"
                  mydr="✓"
                  ours="✓"
                />
                <Row
                  label="SMS przypomnienia T-24h"
                  znl="VIP (699 zł)"
                  mydr="✓"
                  ours="✓"
                />
                <Row
                  label="Wideokonsultacje (add-on)"
                  znl="Plus (499 zł)"
                  mydr="Pro (339 zł)"
                  ours="✓ na życzenie"
                />
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-xs text-text-muted">
            Komentarze i porównanie funkcji na podstawie publicznie dostępnych cenników (czerwiec 2026).
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-3xl rounded-3xl bg-text px-8 py-16 text-center text-bg ring-1 ring-text/20">
          <h2 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Zajmij swój pakiet startowy
          </h2>
          <p className="mt-3 text-base text-bg/80">
            Im wcześniej dołączysz, tym więcej miesięcy w cenie jednego. Pakiet
            założyciela: 12 miesięcy za 99 zł. Tylko 10 miejsc.
          </p>
          <Link
            to="/start"
            className="mt-8 inline-flex rounded-full bg-bg px-6 py-3 text-base font-medium text-text hover:bg-bg/90"
          >
            Stwórz konto →
          </Link>
        </div>
      </section>
    </>
  );
}

function ValueCard({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl bg-bg p-6 ring-1 ring-border">
      <span className="inline-flex size-10 items-center justify-center rounded-full bg-accent-soft text-accent">
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <h3 className="mt-4 font-display text-lg font-semibold text-text">{title}</h3>
      <p className="mt-2 text-sm text-text-muted">{body}</p>
    </div>
  );
}

function Row({
  label,
  znl,
  mydr,
  ours,
}: {
  label: string;
  znl: string;
  mydr: string;
  ours: string;
}) {
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-text">{label}</td>
      <td className="px-4 py-3 text-text-muted">{znl}</td>
      <td className="px-4 py-3 text-text-muted">{mydr}</td>
      <td className="px-4 py-3 font-semibold text-accent">{ours}</td>
    </tr>
  );
}
